import React from "react";
import { Tooltip } from "antd";
import "./style.css";

/** Formats a number for display; null and undefined become an em dash. */
export const fmt = (value, digits = 0) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

/**
 * Percentage change between two figures, with the direction it points.
 * Returns null when there is no comparable base, so the UI shows nothing
 * rather than a misleading "+100%".
 */
export const delta = (current, previous) => {
  if (current === null || current === undefined) return null;
  if (previous === null || previous === undefined || previous === 0) return null;
  const change = ((current - previous) / previous) * 100;
  return { percent: Math.round(change * 10) / 10, up: change >= 0 };
};

/**
 * A headline figure with an optional change against the previous period.
 *
 * `goodWhenUp` is separate from the arrow direction because a rise is not
 * always good news -- more man-days is usually positive, more absence is not.
 */
export const KpiTile = ({ label, value, suffix, foot, change, goodWhenUp = true, tone = "" }) => {
  const positive = change ? change.up === goodWhenUp : null;
  // Some tiles carry a name rather than a figure (largest supplier, for
  // instance). Those need a smaller size that can wrap, or a long contractor
  // name renders at 30px and breaks the row.
  const isText = typeof value === "string" && /[a-zA-Z]{3}/.test(value);
  return (
    <article className={`an-kpi ${tone ? `is-${tone}` : ""}`}>
      <span className="an-kpi-label">{label}</span>
      <span className={`an-kpi-value ${isText ? "is-text" : ""}`} title={isText ? value : undefined}>
        {value}
        {suffix && <em>{suffix}</em>}
      </span>
      {change && (
        <span className={`an-kpi-change ${positive ? "is-good" : "is-bad"}`}>
          {change.up ? "▲" : "▼"} {Math.abs(change.percent)}%
          <span className="an-kpi-change-note">vs previous</span>
        </span>
      )}
      {foot && !change && <span className="an-kpi-foot">{foot}</span>}
    </article>
  );
};

export const KpiRow = ({ children }) => <div className="an-kpis">{children}</div>;

/**
 * States what an hours figure is actually based on.
 *
 * Around half of all historical records have an imputed checkout rather than a
 * measured one. Hours are calculated from measured records only, and this banner
 * makes that visible instead of leaving the reader to assume full coverage.
 */
export const CoverageNote = ({ coverage }) => {
  if (!coverage || !coverage.totalRecords) return null;
  const { measuredRecords, totalRecords, measuredPercent, bySource } = coverage;
  const imputed = (bySource?.CAPPED || 0) + (bySource?.SHIFT_END || 0);
  const invalid = bySource?.INVALID || 0;
  const open = bySource?.OPEN || 0;
  const low = (measuredPercent ?? 100) < 75;

  return (
    <div className={`an-coverage ${low ? "is-warn" : ""}`}>
      <span className="an-coverage-bar" aria-hidden="true">
        <span style={{ width: `${measuredPercent ?? 0}%` }} />
      </span>
      <div className="an-coverage-text">
        <strong>
          Hours are based on {fmt(measuredRecords)} of {fmt(totalRecords)} records
          {measuredPercent !== null && measuredPercent !== undefined
            ? ` (${measuredPercent}%)`
            : ""}
        </strong>
        <span>
          {imputed > 0 && `${fmt(imputed)} had no checkout and were closed by policy. `}
          {invalid > 0 && `${fmt(invalid)} are quarantined as invalid. `}
          {open > 0 && `${fmt(open)} are still open. `}
          Headcount and man-days use every record and are unaffected.
        </span>
      </div>
    </div>
  );
};

export const ChartCard = ({ title, subtitle, actions, children, help }) => (
  <section className="an-card">
    <header className="an-card-head">
      <div>
        <h3>
          {title}
          {help && (
            <Tooltip title={help}>
              <span className="an-info">i</span>
            </Tooltip>
          )}
        </h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="an-card-actions">{actions}</div>}
    </header>
    <div className="an-card-body">{children}</div>
  </section>
);

/** Empty state that names the reason, rather than a bare "no data found". */
export const EmptyState = ({ loading, hasFilters, message }) => (
  <div className="an-empty">
    {loading ? (
      <span className="table-state-spinner" role="status" aria-label="Loading" />
    ) : (
      <>
        <p className="an-empty-title">{message || "Nothing to show for this selection"}</p>
        {hasFilters && (
          <p className="an-empty-hint">
            Try widening the date range, or clearing a filter — sites and contractors marked
            “no data” have never reported attendance.
          </p>
        )}
      </>
    )}
  </div>
);
